import TypeCard, {
  TypeCardImage,
  TypeCardTitle,
  TypeCardPrice,
  TypeCardBenefitList,
  TypeCardHeader,
  TypeCardIngress,
  TypeCardButton,
  ButtonAction,
} from "./TypeCard";
import { useContractTypeUpgradeAction } from "./UpgradeContractButton";
import styled from "styled-components";
import { ContractTypeBenefit } from "./ContractTypeBenefit";
import { ContractType } from "@vehicle-contract/common/lib/model/contract/SalesContract";
import { ContractUpgradeProduct } from "../../payments/PaymentViewModel";
import NokPrice from "@teamscheme/nok-price";
import ErrorMessage from "../../shared/error/ErrorMessage";
import StandardIllustration from "./illustrations/standard_95x125.svg";
import { GetContractUpgradeProductsResponse } from "./ContractTypePage";
import { addOnClick } from "./addOnClick";
import { getContractTypeKey } from "../create-contract/ContractTypeName";

export const STANDARD_BENEFITS: ContractTypeBenefit[] = [
  { label: "BankID", positive: true },
  { label: "Eierskifte hos Statens vegvesen", positive: true },
  {
    label: "NAF prøvemedlemskap med fordeler i 1 måned",
    positive: true,
    excludeMembers: true,
  },
];

export const StandardPackageDetails = () => (
  <>
    <p>
      Denne oppgraderingen gir brukerne tilgang til å signere med BankID og
      trygg lagring av kontrakten hos NAF i to år. Trygg lagring betyr at du har
      tilgang til og kan laste ned kontrakten så lenge NAF lagrer den (to år).
      Du får tilgang til kontrakten gjennom Mitt NAF og kan logge inn med
      opplysningene du har registrert inne i kontrakten.
    </p>
    <p>
      Oppgraderingen inkluderer gratis medlemskap med veihjelp* i NAF for begge
      partene i 1 måned.** Den parten som betaler vil automatisk få aktivert
      medlemskapet. Medlemskapet utløper automatisk etter en måned, og du
      trenger ikke foreta deg noe for å avslutte det. Medlemskapet gir ikke
      gyldig stemme og/eller mulighet for å delta i organisasjonsarbeidet til
      NAF.
    </p>
    <p>
      Tjenesten for eierskifte med NAF Kjøpekontrakt tilgjengeliggjøres med et
      tredjeparts-API levert av Statens vegvesen som muliggjør signering av
      eierskifte samtidig med signering av kjøpekontrakten. Du må ha betalt for
      Premium eller Standard for å ha tilgang til denne tjenesten. Ved tekniske
      feil eller andre feil som gjør at tjenesten ikke kan gjennomføres blir
      brukeren guidet til å fullføre eierskifte i Statens vegvesen sin
      selvbetjeningsløsning.
    </p>
    <p>
      * Veihjelp gjelder kun personbiler, ikke andre kjøretøy
      <br />
      ** Gjelder ikke eksisterende medlemmer eller firma/bedrifter.
    </p>
  </>
);

export interface StandardCardProps {
  product: ContractUpgradeProduct;
  className?: string;
  buttonLabel?: string;
  buttonAction: ButtonAction;
  error?: boolean;
}

const StandardCard = styled(function (props: StandardCardProps) {
  const { product, className, buttonLabel, buttonAction, error } = props;
  const typeKey = getContractTypeKey(product.contractType);
  return (
    <TypeCard className={className}>
      <TypeCardHeader>
        <TypeCardImage
          src={StandardIllustration}
          alt="Standard"
          width="91"
          height="124"
        />
        <TypeCardTitle>Standard</TypeCardTitle>
        <TypeCardIngress>
          Kontrakt med BankID identifisering og signering, registrering av
          eierskifte, m.m.
        </TypeCardIngress>
      </TypeCardHeader>
      <TypeCardBenefitList benefits={STANDARD_BENEFITS} />
      <TypeCardPrice>
        <NokPrice value={product.price} />
      </TypeCardPrice>
      <TypeCardButton typeKey={typeKey} action={buttonAction}>
        {buttonLabel}
      </TypeCardButton>
      {error ? (
        <ErrorMessage>En feil har oppstått! Prøv igjen.</ErrorMessage>
      ) : null}
    </TypeCard>
  );
})``;

export default StandardCard;

export const StandardUpgradeCard = styled(function ({
  upgrade,
  returnPath,
  className,
  onOpenPayment,
}: {
  upgrade: GetContractUpgradeProductsResponse;
  returnPath?: string;
  className?: string;
  onOpenPayment?: () => void;
}) {
  const client = useContractTypeUpgradeAction(upgrade, ContractType.Standard, returnPath);

  const { error, action } = client;

  const baseProps = {
    className,
    product: client.product,
    buttonLabel: "Til betaling",
    buttonAction: onOpenPayment ? addOnClick(action, onOpenPayment) : action,
    error: !!error,
  };

  return <StandardCard {...baseProps} />;
})``;
